import { Input } from '@avanta/shared/components/ui/Input';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { TOP_ONLY_SHADOW_CLASS_NAME } from '@avanta/shared/constants/styles';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useFetchSearchPsychologist } from '@avanta/shared/hooks/useFetchSearchPsychologist';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { type Psychologist } from '@avanta/shared/types/psychologists';
import { cn } from '@avanta/shared/utils/cn';
import { Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StepBackButton } from 'pages/AppointmentsReserve/components/ui/StepBackButton';
import { StepSection } from 'pages/AppointmentsReserve/components/ui/StepSection';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData, type StepProps } from 'pages/AppointmentsReserve/types';
import { changeFlow, resetAvailabilitySelection } from 'pages/AppointmentsReserve/utils';
import { PsychologistSearchResults } from './components/PsychologistSearchResults';
import { SEARCH_PSYCHOLOGISTS_KEY_PREFIX } from './constants';

export const PsychologistStep = ({ onNext }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const { control, setValue, getValues } = useFormContext<AppointmentsReserveFormData>();
  const [contractId, patient, flow] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.contractId,
      AppointmentsReservePayload.patient,
      AppointmentsReservePayload.flow,
    ],
  });
  const patientId = patient?.id ?? '';
  const { data: psychologists = [], isLoading } = useFetchSearchPsychologist({
    name: debouncedSearch,
    insurance: true,
    contractId,
    patientId,
  });
  const listRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(listRef, 'element', psychologists);
  const showScrollShadow = hasOverflow && !isAtBottom;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value);
  };

  const handlePsychologistSelect = (psychologist: Psychologist): void => {
    if (psychologist.id !== getValues(AppointmentsReservePayload.psychologist)?.id) {
      resetAvailabilitySelection(setValue, flow);
    }
    setValue(AppointmentsReservePayload.psychologist, psychologist);
    onNext();
  };

  const handleBack = (): void => {
    setValue(AppointmentsReservePayload.dateTime, '');
    setValue(AppointmentsReservePayload.psychologist, undefined);
    changeFlow(setValue, flow, FLOW_MODES.regular);
  };

  return (
    <div className="h-full relative w-4xl flex flex-col items-center">
      <StepSection
        title={t(`${SEARCH_PSYCHOLOGISTS_KEY_PREFIX}title`)}
        className="flex flex-col min-h-0 gap-5"
        containerClassName="w-xl h-full"
      >
        <Input
          aria-label={t(`${SEARCH_PSYCHOLOGISTS_KEY_PREFIX}search.placeholder`)}
          placeholder={t(`${SEARCH_PSYCHOLOGISTS_KEY_PREFIX}search.placeholder`)}
          leftIcon={Search}
          onChange={handleSearchChange}
          className="w-full max-w-137.5"
        />
        <div className="relative min-h-0 w-full max-w-138 flex-1">
          <div
            ref={listRef}
            className="scrollbar-custom flex h-full min-h-0 w-full flex-col gap-4 overflow-y-auto pb-3"
          >
            <PsychologistSearchResults
              psychologists={psychologists}
              isLoading={isLoading}
              onPsychologistSelect={handlePsychologistSelect}
            />
          </div>
          {showScrollShadow ? (
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-white',
                TOP_ONLY_SHADOW_CLASS_NAME
              )}
            />
          ) : null}
        </div>
      </StepSection>
      <StepBackButton className="absolute bottom-5 left-2" onClick={handleBack} />
    </div>
  );
};
